import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import { useTypingSearchAI } from '@/hooks/useTypingSearchAI'
import { showTemplateRecentButton } from '@/utils/data/feeds'
import { Search } from '@mynaui/icons-react'
import { Stack, Text } from './custom-components'
import { Loader } from './custom-components/Loader'
import { ScrollArea } from './shadcn/scroll-area'
import { AdCard, DiscoverAdCard } from './templates/AdCard'
import { EmailFeedAdCard, LandingAdCard, LandingFeedAdCard } from './templates/LandingAdCard'

const TypingSearchViewAI = ({
  searchTerm,
  type,
}: {
  searchTerm: string
  type: 'templates' | 'inspirations'
}) => {
  const {
    isLoading,
    isFetchingNextPage,
    adTemplatesOnly,
    emailTemplatesOnly,
    landerTemplatesOnly,
    adInspirationsOnly,
    emailInspirationsOnly,
    landerInspirationsOnly,
    loadMoreRef,
    squareRef,
    desiredCardWidth,
    gutterWidth,
  } = useTypingSearchAI(searchTerm, type)

  const resultCount =
    type === 'templates'
      ? adTemplatesOnly.length + emailTemplatesOnly.length + landerTemplatesOnly.length
      : adInspirationsOnly.length + emailInspirationsOnly.length + landerInspirationsOnly.length

  return (
    <ScrollArea type="always" className="w-full flex-1 max-h-[70svh] lg:max-h-[85svh] pb-0">
      {isLoading ? (
        <Stack className="h-full gap-3 w-full flex flex-col justify-center items-center">
          <Loader />
        </Stack>
      ) : resultCount === 0 ? (
        <Stack className="gap-3 w-full py-60 items-center">
          <Search className="h-16 w-16 text-gray-300" aria-hidden="true" />
          <Text weight="normal" className="text-gray-300 text-5xl text-center w-full">
            No results found
          </Text>
        </Stack>
      ) : (
        <Stack className="gap-3 w-full mb-10 !pr-6">
          <div ref={squareRef} className="relative w-full lg:px-0 pb-10">
            <TypedResponsiveMasonry
              columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: type === 'templates' ? 4 : 3 }}
            >
              <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                {type === 'templates' &&
                  adTemplatesOnly.map((template) => (
                    <AdCard
                      key={`ad-${template.assetId}`}
                      adData={{
                        Ad: {
                          'Canva Template LInk': template['Canva Template LInk'] ?? '',
                          'Creative ID': `${template.CreativeID}`,
                          'Example Image': [],
                          'Template Image': [],
                          AdDbId: parseInt(template.assetId.toString()),
                          atID: `${template.assetId}`,
                          Created: template.createdAt,
                          CreativeID: template.CreativeID ?? 0,
                          imageUrl: template.imageUrl,
                          Ready: true,
                          form: '',
                          Liked: false,
                          Status: '',
                        },
                        IsLocked: false,
                        IsPublic: false,
                        brandName: template.brandName ?? undefined,
                        shouldInvalidateCache: false,
                        DesiredWidth: desiredCardWidth,
                      }}
                      showRecentButton={showTemplateRecentButton(template.createdAt)}
                    />
                  ))}

                {type === 'templates' &&
                  emailTemplatesOnly.map((template) => (
                    <LandingAdCard
                      key={`email-${template.assetId}`}
                      adData={{
                        Screenshot: template.imageUrl ?? '',
                        ID: template.assetId.toString(),
                        id: parseInt(template.assetId.toString()),
                        created: template.createdAt,
                        imageUrl: template.imageUrl,
                      }}
                      showRecentButton={showTemplateRecentButton(template.createdAt)}
                      type="email"
                    />
                  ))}

                {type === 'templates' &&
                  landerTemplatesOnly.map((template) => (
                    <LandingAdCard
                      key={`lander-${template.assetId}`}
                      adData={{
                        Screenshot: template.imageUrl ?? '',
                        ID: template.assetId.toString(),
                        id: parseInt(template.assetId.toString()),
                        created: template.createdAt,
                        imageUrl: template.imageUrl,
                      }}
                      showRecentButton={showTemplateRecentButton(template.createdAt)}
                      type="landing-page"
                    />
                  ))}

                {type === 'inspirations' &&
                  adInspirationsOnly.map((inspiration) => (
                    <DiscoverAdCard
                      key={`iad-${inspiration.assetId}`}
                      DesiredWidth={desiredCardWidth}
                      adData={{
                        id: parseInt(inspiration.assetId.toString()),
                        reach: null,
                        earliestView: '',
                        adRunningDays: 0,
                        imageUrl: inspiration.imageUrl ?? '',
                        brandId: inspiration.brandId ?? '',
                        brandName: inspiration.brandName ?? '',
                        brandImage: inspiration.brandName ?? '',
                        videoUrl: inspiration.videoUrl ?? '',
                        isSaved: false,
                      }}
                      IsPublic={false}
                      IsLocked={false}
                    />
                  ))}

                {type === 'inspirations' &&
                  emailInspirationsOnly.map((inspiration) => (
                    <EmailFeedAdCard
                      key={`eia-${inspiration.assetId}`}
                      adData={{
                        brandId: inspiration.brandId ?? '',
                        brandImage: inspiration.brandImage ?? '',
                        brandName: inspiration.brandName ?? '',
                        id: parseInt(inspiration.assetId.toString()),
                        imageUrl: inspiration.imageUrl ?? '',
                        requestCount: 0,
                        template: null,
                        date: inspiration.createdAt ?? '',
                        homepage: '',
                        subject: '',
                        to: '',
                      }}
                    />
                  ))}

                {type === 'inspirations' &&
                  landerInspirationsOnly.map((inspiration) => (
                    <LandingFeedAdCard
                      key={`lia-${inspiration.assetId}`}
                      adData={{
                        brandId: inspiration.brandId ?? '',
                        brandImage: inspiration.brandImage ?? '',
                        brandName: inspiration.brandName ?? '',
                        landerId: parseInt(inspiration.assetId.toString()),
                        desktopScreenshot: inspiration.desktopScreenshot ?? '',
                        mobileScreenshot: inspiration.mobileScreenshot ?? '',
                        requestCount: 0,
                        template: null,
                        createdAt: inspiration.createdAt ?? '',
                        landerUrl: '',
                      }}
                    />
                  ))}
              </MasonryComponent>
            </TypedResponsiveMasonry>
          </div>

          <div ref={loadMoreRef} className="flex justify-center w-full min-h-14 h-14">
            {isFetchingNextPage && <Loader />}
          </div>
        </Stack>
      )}
    </ScrollArea>
  )
}

export default TypingSearchViewAI
